import { canEditFeedbackForJobStartDate } from './surveyPopupEligibility';

const normalizeFlag = (value?: boolean | number | string | null) =>
  value === true || value === 1 || value === '1';

const normalizeRating = (value?: number | string | null) => {
  const rating = Number(value);
  return Number.isFinite(rating) ? rating : 0;
};

interface TalentSubmittedReview {
  submissionStatus?: number | string | null;
  overallRating?: number | string | null;
  friendlinessRating?: number | string | null;
  infectionControlRating?: number | string | null;
  equipmentRating?: number | string | null;
  preparationRating?: number | string | null;
  collaborationPreference?: number | string | null;
  additionalFeedback?: string | null;
}

export interface TalentSubmittedFeedbackLike extends TalentSubmittedReview {
  dentistRating?: number | string | null;
  talentReviewSubmitted?: boolean | number | string | null;
  talentReview?: TalentSubmittedReview | null;
}

const hasSubmittedTalentReviewContent = (review?: TalentSubmittedReview | null) => {
  if (!review || Number(review.submissionStatus) !== 1) return false;
  return normalizeRating(review.overallRating) > 0
    || normalizeRating(review.friendlinessRating) > 0
    || normalizeRating(review.infectionControlRating) > 0
    || normalizeRating(review.equipmentRating) > 0
    || normalizeRating(review.preparationRating) > 0
    || normalizeRating(review.collaborationPreference) > 0
    || !!review.additionalFeedback?.trim();
};

/** Real office→talent submitted feedback (not skip/dismiss-only). */
export function hasRealDentistSubmittedFeedback(jobLike: {
  hygienistRating?: number | string | null;
  feedbackSubmitted?: boolean | number | string | null;
}): boolean {
  return normalizeRating(jobLike.hygienistRating) > 0 || normalizeFlag(jobLike.feedbackSubmitted);
}

/** Real talent→office submitted feedback; dismiss/defer-only rows do not qualify. */
export function hasRealTalentSubmittedFeedback(jobLike: TalentSubmittedFeedbackLike): boolean {
  return normalizeRating(jobLike.dentistRating) > 0
    || normalizeFlag(jobLike.talentReviewSubmitted)
    || hasSubmittedTalentReviewContent(jobLike)
    || hasSubmittedTalentReviewContent(jobLike.talentReview);
}

/**
 * T2-6961: editing submitted feedback is removed. Always false.
 * Kept so older call sites / tests compile during the cutover.
 */
export function canEditDentistFeedback(_jobLike: {
  hygienistRating?: number | string | null;
  feedbackSubmitted?: boolean | number | string | null;
  startDate?: string | Date | null;
}): boolean {
  return false;
}

export function canEditTalentFeedback(_jobLike: {
  dentistRating?: number | string | null;
  startDate?: string | Date | null;
  shifts?: Array<{ shiftDate?: string | Date | null }>;
}): boolean {
  return false;
}

/** Unsubmitted DO survey may still be completed within the shared 30-day Toronto window. */
export function canCompleteDentistSurvey(jobLike: {
  hygienistRating?: number | string | null;
  feedbackSubmitted?: boolean | number | string | null;
  startDate?: string | Date | null;
}): boolean {
  if (hasRealDentistSubmittedFeedback(jobLike)) return false;
  return canEditFeedbackForJobStartDate(jobLike.startDate);
}

export function canCompleteTalentSurvey(jobLike: TalentSubmittedFeedbackLike & {
  startDate?: string | Date | null;
  shifts?: Array<{ shiftDate?: string | Date | null }>;
}): boolean {
  if (hasRealTalentSubmittedFeedback(jobLike)) return false;
  const startDate = jobLike.startDate || jobLike.shifts?.[0]?.shiftDate;
  return canEditFeedbackForJobStartDate(startDate);
}

/** Parse viewOnly from React Router location.state or a query string (T2-6961). */
export function resolveFeedbackViewOnlyMode(opts: {
  state?: { viewOnly?: unknown; editMode?: unknown; isEdit?: unknown } | null;
  search?: string | null;
}): boolean {
  const fromState = opts.state?.viewOnly === true
    || opts.state?.viewOnly === 1
    || opts.state?.viewOnly === '1'
    || opts.state?.viewOnly === 'true'
    // Legacy edit flags now open view-only (edit capability removed).
    || opts.state?.editMode === true
    || opts.state?.editMode === 1
    || opts.state?.editMode === '1'
    || opts.state?.editMode === 'true'
    || opts.state?.isEdit === true
    || opts.state?.isEdit === 1
    || opts.state?.isEdit === '1'
    || opts.state?.isEdit === 'true';
  if (fromState) return true;
  if (!opts.search) return false;
  try {
    const params = new URLSearchParams(opts.search.startsWith('?') ? opts.search : `?${opts.search}`);
    const q = params.get('viewOnly') ?? params.get('editMode') ?? params.get('isEdit');
    return q === '1' || q === 'true';
  } catch {
    return false;
  }
}

/**
 * @deprecated T2-6961 — edit mode removed; maps to view-only.
 */
export function resolveFeedbackEditMode(opts: {
  state?: { editMode?: unknown; isEdit?: unknown; viewOnly?: unknown } | null;
  search?: string | null;
}): boolean {
  return resolveFeedbackViewOnlyMode(opts);
}
